import { createFileRoute } from '@tanstack/react-router'
import { useEnvironment } from '@strac/react-client-environment'
import { APIProvider } from '@vis.gl/react-google-maps'
import MapSettingsContextProvider from '../../../contexts/MapSettingsContext/MapSettingsContextProvider'
import React, { useContext } from 'react'
import Map from '../../../views/Map/Map'
import { getAllData } from '../../../utils/data'
import useRouteLog from '../../hooks/useRouteLog'
import { auth } from '../../hooks/beforeLoad/auth'
import useLocalStorageValue from '../../../views/Map/RightSider/Options/hooks/useLocalStroageValue'
import useVariableListener from '../../hooks/useVariableListener'
import LoadingScreen from '../../LoadingScreen/LoadingScreen'
import { AuthContext } from '@strac/react-client-auth'

export const Route = createFileRoute('/map/')({
  beforeLoad: auth,
  pendingComponent: () => <LoadingScreen />,
  loader: async ({ context }) => {
    const label = 'map/index.tsx loader'
    console.time(label)
    const { authUser, client, queryClient } = context

    /* Allowed Agencies */
    const allowedAgencies = Array.from(
      new Set(authUser?.allowed_agencies.split(',').map((agencyId: string) => parseInt(agencyId)))
    )

    /*
    options: 1_day, 1_week, 1_month, 1_year, all
     */
    const assettrakTimeFilter = window.localStorage.getItem('assettrak_time_filter') || '1_day' // default to 1 day

    const vehicleVehicleLocationsQuery = {
      allowed_agencies: allowedAgencies,
      timeframe: assettrakTimeFilter
    }

    const vehicleVehicleLocations = await queryClient.ensureQueryData({
      queryKey: ['vehicle-vehicle-location', 'find', vehicleVehicleLocationsQuery],
      queryFn: () =>
        client.service('vehicle-vehicle-location').find({
          query: vehicleVehicleLocationsQuery
        }),
      gcTime: 0,
      staleTime: 0
    })

    /* Agencies */
    const agenciesQuery = {
      id: {
        $in: allowedAgencies
      }
    }
    const agencies = await queryClient.ensureQueryData({
      queryKey: ['agencies', 'find', agenciesQuery],
      queryFn: () => getAllData(client, 'agencies', agenciesQuery)
    })

    /* Vehicle Types */
    const vehicleTypeQuery = {}
    const vehicleTypes = await queryClient.ensureQueryData({
      queryKey: ['vehicle-type', 'find', vehicleTypeQuery],
      queryFn: () => getAllData(client, 'vehicle-type', vehicleTypeQuery)
    })

    /* Device Types */
    const deviceTypesQuery = {}
    const deviceTypes = await queryClient.ensureQueryData({
      queryKey: ['device-type', 'find', deviceTypesQuery],
      queryFn: () => getAllData(client, 'device-type', deviceTypesQuery)
    })

    /* ADSB */
    const adsbQuery = {
      adsb_position_updates: 1
    }
    const adsb = await queryClient.ensureQueryData({
      queryKey: ['unit-location', 'find', adsbQuery],
      queryFn: () => getAllData(client, 'unit-location', adsbQuery)
    })

    console.timeEnd(label)
    return {
      vehicles: vehicleVehicleLocations.vehicles,
      vehicleLocations: vehicleVehicleLocations.vehicle_locations,
      deviceTypes,
      agencies,
      vehicleTypes,
      adsb
    }
  },
  component: RouteComponent
})

function RouteComponent() {
  const { REACT_APP_GOOGLE_MAPS_API_KEY } = useEnvironment()
  const onLoad = () => {
    console.log('@vis.gl: Google Maps API loaded, version:', google.maps.version)
  }
  useRouteLog('/map/index.tsx')

  const { authUser } = useContext(AuthContext) as any

  const timeKey = useLocalStorageValue('assettrak_time_filter')
  useVariableListener(timeKey)

  return (
    <APIProvider apiKey={REACT_APP_GOOGLE_MAPS_API_KEY || ''} onLoad={onLoad}>
      <MapSettingsContextProvider>
        <Map userId={authUser?.id} />
      </MapSettingsContextProvider>
    </APIProvider>
  )
}
